import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tabs";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { AlertCircle, FileText, Loader2, Rocket, Wrench } from "lucide-react";
import { useMemo } from "react";
import { useProjectPermissions } from "@/app/capabilities/api/hooks";
import { PROJECT } from "@/app/capabilities/permissions";
import { columns } from "@/app/tools/data-table/columns";
import { ToolsTable } from "@/app/tools/data-table/tools-table";
import {
  useGetDeploymentStatus,
  useGetServer,
  useGetServerTools,
} from "../api/hooks";
import { ServerInfo } from "./info";
import { LogsViewer } from "./logs-viewer";

export const ServerDetails = () => {
  const navigate = useNavigate({
    from: "/orgs/$organizationId/projects/$projectId/servers/$serverId",
  });
  const { serverId } = useParams({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/servers/$serverId",
  });

  const { section } = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/servers/$serverId",
  });

  const { data: server } = useGetServer(serverId);

  const isManaged = server?.managed;

  const { data: deploymentStatusData } = useGetDeploymentStatus({
    id: serverId,
    enabled: isManaged ?? false,
    activeInterval: 2000,
    stableInterval: 30_000,
  });

  const deploymentStatus = deploymentStatusData?.status;
  const isDeploymentFailed = isManaged && deploymentStatus === "failed";
  const isToolsAvailable =
    !isManaged || (isManaged && deploymentStatus === "running");

  const {
    data: tools,
    isLoading: isToolsLoading,
    isError,
    error,
  } = useGetServerTools(serverId, {
    enabled: isToolsAvailable,
  });

  const { canProject } = useProjectPermissions();
  const shouldShowLogs =
    server?.managed && canProject(PROJECT.deployments.read);

  const ToolsTableComponent = useMemo(() => {
    if (isToolsLoading) {
      return (
        <div className="flex h-full items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      );
    }

    if (!isToolsAvailable) {
      if (isDeploymentFailed) {
        return (
          <div className="flex h-full flex-col items-center justify-center gap-6 py-16">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <div className="flex flex-col items-center gap-2 text-center">
              <h3 className="font-semibold text-base">Deployment failed</h3>
              <p className="max-w-md text-muted-foreground text-sm">
                The server deployment failed. Check the Logs tab for details.
              </p>
            </div>
          </div>
        );
      }

      return (
        <div className="flex h-full flex-col items-center justify-center gap-6 py-16">
          <div className="flex items-center gap-4">
            <Rocket className="h-12 w-12 text-muted-foreground/60" />
            <Loader2 className="h-8 w-8 animate-spin text-brand-text" />
          </div>
          <div className="flex flex-col items-center gap-2 text-center">
            <h3 className="font-semibold text-base">
              Tools are deploying and will be available soon
            </h3>
            <p className="max-w-md text-muted-foreground text-sm">
              Your server is currently being set up. Once the deployment
              completes, you'll be able to access all available tools here.
            </p>
          </div>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 py-8">
          <AlertCircle className="h-6 w-6 text-destructive" />
          <p className="text-muted-foreground text-sm">
            Failed to load server tools
          </p>
          <p className="text-muted-foreground text-sm">{error?.message}</p>
        </div>
      );
    }

    return (
      <ToolsTable
        columns={columns}
        data={tools?.items ?? []}
        stickyColumnBg="card"
      />
    );
  }, [
    isToolsAvailable,
    isToolsLoading,
    isError,
    tools,
    error,
    isDeploymentFailed,
  ]);

  const handleSectionChange = (value: "tools" | "logs") => {
    navigate({
      from: "/orgs/$organizationId/projects/$projectId/servers/$serverId",
      to: ".",
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      search: {
        section: value,
      } as never,
    });
  };

  return (
    <div className="flex flex-col gap-8 p-4">
      {/* Main Server Info Card */}
      <ServerInfo numTools={tools?.items.length ?? 0} server={server} />

      {shouldShowLogs ? (
        <div className="flex flex-col gap-4">
          <Tabs className="w-full" defaultValue={section}>
            <TabsList className="justify-start gap-4">
              <TabsTrigger
                className="flex items-center gap-3"
                onClick={() => handleSectionChange("tools")}
                value="tools"
              >
                <Wrench className="size-4 text-muted-foreground" />
                <span className="font-medium text-sm">Tools</span>
              </TabsTrigger>
              <TabsTrigger
                className="flex items-center gap-3"
                onClick={() => handleSectionChange("logs")}
                value="logs"
              >
                <FileText className="size-4 text-muted-foreground" />
                <span className="font-medium text-sm">Logs</span>
              </TabsTrigger>
            </TabsList>
            <TabsContent
              className="mt-4 rounded-md border shadow-xs"
              value="tools"
            >
              <div className="p-4">{ToolsTableComponent}</div>
            </TabsContent>
            <TabsContent className="mt-4" value="logs">
              <LogsViewer
                deploymentStatus={deploymentStatus}
                isManaged={server?.managed}
                serverId={serverId}
              />
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        ToolsTableComponent
      )}
    </div>
  );
};
